import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import ScreenLoading from 'components/ScreenLoading'
import api from 'containers/api'

interface UnifiedSession {
  type: 'login' | 'mcp'
  session_id: string
  device_name: string
  device_type?: string
  browser?: string
  os?: string
  platform?: string
  ip_address?: string
  location?: string
  organization_name?: string
  created_at: string
  last_active: string
  is_current: boolean
  active_sessions?: number
  device_id?: string
}

const DevicesTab = () => {
  const token = useSelector(selectToken)
  const [sessions, setSessions] = useState<UnifiedSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchAllSessions()
  }, [])

  const fetchAllSessions = async () => {
    try {
      setLoading(true)
      setError('')

      // Fetch both device sessions and MCP sessions in parallel
      const [devicesData, mcpData] = await Promise.all([
        api.getDevices({ token }).catch(() => ({ devices: [] })),
        api.getMCPSessions({ token }).catch(() => [])
      ])

      // Transform device sessions
      const loginSessions: UnifiedSession[] = (devicesData.devices || []).map((device: any) => ({
        type: 'login' as const,
        session_id: device.device_id,
        device_name: device.device_name,
        device_type: device.device_type,
        browser: device.browser,
        os: device.os,
        ip_address: device.ip_address,
        location: device.location,
        created_at: device.created_at,
        last_active: device.last_active,
        is_current: device.is_current,
        active_sessions: device.active_sessions,
        device_id: device.device_id
      }))

      // Transform MCP sessions
      const mcpSessions: UnifiedSession[] = (mcpData || []).map((s: any) => ({
        type: 'mcp' as const,
        session_id: s.session_id,
        device_name: s.device_name || `${s.platform} App`,
        platform: s.platform,
        ip_address: s.ip_address,
        organization_name: s.organization_name,
        created_at: s.created_at,
        last_active: s.last_used_at,
        is_current: false
      }))

      // Combine and sort by last_active
      const allSessions = [...loginSessions, ...mcpSessions].sort((a, b) =>
        new Date(b.last_active).getTime() - new Date(a.last_active).getTime()
      )

      setSessions(allSessions)
    } catch (err) {
      console.error('Error fetching sessions:', err)
      setError('Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeSession = async (session: UnifiedSession) => {
    const confirmMessage = session.type === 'mcp'
      ? `Are you sure you want to revoke this MCP session for ${session.device_name}?`
      : `Are you sure you want to revoke all sessions for ${session.device_name}?`

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setError('')
      if (session.type === 'mcp') {
        await api.revokeMCPSession({ sessionId: session.session_id, token })
      } else {
        await api.revokeDevice({ deviceId: session.session_id, token })
      }
      await fetchAllSessions()
    } catch (err) {
      console.error('Error revoking session:', err)
      setError('Failed to revoke session')
    }
  }

  const handleDeleteDevice = async (deviceId: string, deviceName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${deviceName}?`)) {
      return
    }

    try {
      setError('')
      await api.deleteDevice({ deviceId, token })
      await fetchAllSessions()
    } catch (err) {
      console.error('Error deleting device:', err)
      setError('Failed to delete device')
    }
  }

  const getDeviceIcon = (session: UnifiedSession): string => {
    if (session.type === 'mcp') {
      return 'code'
    }
    switch (session.device_type?.toLowerCase()) {
      case 'mobile':
        return 'smartphone'
      case 'tablet':
        return 'tablet'
      case 'desktop':
        return 'computer'
      default:
        return 'devices'
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '900px', padding: '0 1rem', boxSizing: 'border-box' }}>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          borderRadius: '4px',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="material-icons-outlined" style={{ color: '#0c5460' }}>
            security
          </span>
          <strong style={{ color: '#0c5460' }}>Security Notice</strong>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', color: '#0c5460', fontSize: '0.9rem', wordBreak: 'break-word' }}>
          Review your active sessions regularly. This includes both login sessions and MCP (AI assistant) sessions. If you see an unfamiliar session, revoke its access immediately.
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '1rem',
            color: '#721c24',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <ScreenLoading />
        </div>
      ) : sessions.length === 0 ? (
        <div
          style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
          }}
        >
          <span
            className="material-icons-outlined"
            style={{ fontSize: '4rem', color: '#6c757d' }}
          >
            devices
          </span>
          <p style={{ marginTop: '1rem', color: '#6c757d' }}>
            No active sessions found. Sessions will appear here after you log in or connect via MCP.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {sessions.map((session) => (
            <div
              key={session.session_id}
              style={{
                padding: '1.5rem',
                border: session.is_current ? '2px solid #28a745' : '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                position: 'relative',
              }}
            >
              {session.is_current && (
                <div
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  CURRENT
                </div>
              )}

              <div className="device-container" style={{ display: 'flex', gap: '1rem' }}>
                <span
                  className="material-icons-outlined"
                  style={{ fontSize: '3rem', color: '#495057' }}
                >
                  {getDeviceIcon(session)}
                </span>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, wordBreak: 'break-word' }}>{session.device_name}</h4>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: session.type === 'mcp' ? '#e3f2fd' : '#f5f5f5',
                        color: session.type === 'mcp' ? '#1976d2' : '#666',
                      }}
                    >
                      {session.type === 'mcp' ? `MCP (${session.platform})` : 'Login Session'}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.5rem',
                      fontSize: '0.85rem',
                      color: '#6c757d',
                    }}
                  >
                    {session.type === 'login' ? (
                      <>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Browser:</strong> {session.browser}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>OS:</strong> {session.os}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Location:</strong> {session.location || 'Unknown'}
                        </div>
                        <div style={{ wordBreak: 'break-all' }}>
                          <strong>IP:</strong> {session.ip_address || 'Unknown'}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Last Active:</strong> {formatDate(session.last_active)}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Active Sessions:</strong> {session.active_sessions}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Organization:</strong> {session.organization_name}
                        </div>
                        <div style={{ wordBreak: 'break-all' }}>
                          <strong>IP:</strong> {session.ip_address || 'Unknown'}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Last Active:</strong> {formatDate(session.last_active)}
                        </div>
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Created:</strong> {formatDate(session.created_at)}
                        </div>
                      </>
                    )}
                  </div>

                  {!session.is_current && (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="submit-button"
                        style={{
                          backgroundColor: '#ffc107',
                          padding: '0.4rem 1rem',
                          fontSize: '0.85rem',
                          minWidth: '120px',
                        }}
                        onClick={() => handleRevokeSession(session)}
                      >
                        Revoke Access
                      </button>
                      {session.type === 'login' && (
                        <button
                          type="button"
                          className="submit-button"
                          style={{
                            backgroundColor: '#dc3545',
                            padding: '0.4rem 1rem',
                            fontSize: '0.85rem',
                            minWidth: '120px',
                          }}
                          onClick={() => handleDeleteDevice(session.device_id!, session.device_name)}
                        >
                          Delete Device
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DevicesTab
